import PageHeader from "@/components/shared/PageHeader";
import LoadingSkeleton from "@/components/shared/LoadingSkeleton";
import ErrorState from "@/components/shared/ErrorState";
import EmptyState from "@/components/shared/EmptyState";
import { useTerritorialIndicatorsSummary } from "@/hooks";
import PreliminaryDataBanner from "../inspecoes/components/PreliminaryDataBanner";

export default function TerritorioIndicadoresPage() {
  const { data: summary, loading, error } = useTerritorialIndicatorsSummary();

  const hasAnyData =
    !!summary &&
    (summary.avgTreeCoverageByNeighborhood.length > 0 ||
      summary.heatIslandRanking.length > 0 ||
      summary.estimatedTreesByBlock.length > 0 ||
      summary.plantingPotentialByRoad.length > 0 ||
      summary.avgImpermeabilization.length > 0 ||
      summary.treeDeficitRanking.length > 0);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Território > Indicadores"
        subtitle="Indicadores estatísticos consolidados a partir das inspeções territoriais (cobertura arbórea, ilha de calor, potencial de plantio, déficit arbóreo, impermeabilização)."
      />

      <PreliminaryDataBanner />

      {loading && <LoadingSkeleton type="kpi-grid" count={6} />}
      {!loading && error && <ErrorState message="Não foi possível carregar os indicadores territoriais." />}
      {!loading && !error && !hasAnyData && (
        <EmptyState
          icon="ri-bar-chart-2-line"
          title="Nenhum indicador territorial disponível ainda"
          description="Os indicadores são calculados a partir das inspeções territoriais importadas. Importe inspeções em Território > Inspeções para vê-los aqui."
        />
      )}

      {!loading && !error && summary && hasAnyData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-card border border-background-200/70 p-4">
            <h3 className="text-sm font-semibold text-foreground-800 mb-3">Cobertura arbórea média por bairro</h3>
            <div className="space-y-1.5">
              {summary.avgTreeCoverageByNeighborhood.map((row, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-background-50 rounded-md px-3 py-2">
                  <span className="text-foreground-700">{row.neighborhood || "—"}</span>
                  <span className="font-medium text-foreground-800">
                    {row.avgCanopyCoveragePercent != null ? `${row.avgCanopyCoveragePercent}%` : "—"} ·{" "}
                    {row.totalInspections} inspeções
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-card border border-background-200/70 p-4">
            <h3 className="text-sm font-semibold text-foreground-800 mb-3">Ranking de ilha de calor por quadra</h3>
            <div className="space-y-1.5">
              {summary.heatIslandRanking.map((row, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-background-50 rounded-md px-3 py-2">
                  <span className="text-foreground-700">{row.blockCode || "—"}</span>
                  <span className="font-medium text-foreground-800">
                    {row.heatIslandRiskScore != null ? row.heatIslandRiskScore.toFixed(0) : "—"} ({row.riskLevel || "—"})
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-card border border-background-200/70 p-4">
            <h3 className="text-sm font-semibold text-foreground-800 mb-3">Árvores estimadas por quadra</h3>
            <div className="space-y-1.5">
              {summary.estimatedTreesByBlock.map((row, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-background-50 rounded-md px-3 py-2">
                  <span className="text-foreground-700">{row.blockCode || "—"}</span>
                  <span className="font-medium text-foreground-800">
                    {row.estimatedTreeCount} árvores · {row.riskTreeCount} em risco
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-card border border-background-200/70 p-4">
            <h3 className="text-sm font-semibold text-foreground-800 mb-3">Potencial de plantio por via</h3>
            <div className="space-y-1.5">
              {summary.plantingPotentialByRoad.map((row, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-background-50 rounded-md px-3 py-2">
                  <span className="text-foreground-700">{row.roadName || "—"}</span>
                  <span className="font-medium text-foreground-800">{row.totalPlantingPotential} árvores</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-card border border-background-200/70 p-4">
            <h3 className="text-sm font-semibold text-foreground-800 mb-3">Impermeabilização média por bairro</h3>
            <div className="space-y-1.5">
              {summary.avgImpermeabilization.map((row, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-background-50 rounded-md px-3 py-2">
                  <span className="text-foreground-700">{row.neighborhood || "—"}</span>
                  <span className="font-medium text-foreground-800">
                    {row.avgImpermeabilizationPercent != null ? `${row.avgImpermeabilizationPercent}%` : "—"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-card border border-background-200/70 p-4">
            <h3 className="text-sm font-semibold text-foreground-800 mb-3">Déficit arbóreo por quadra</h3>
            <div className="space-y-1.5">
              {summary.treeDeficitRanking.map((row, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-background-50 rounded-md px-3 py-2">
                  <span className="text-foreground-700">{row.blockCode || "—"}</span>
                  <span className="font-medium text-foreground-800">
                    déficit {row.treeDeficitCount} · potencial {row.plantingPotentialCount}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
